import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grow } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useHelpAssistant } from "../../services/mutations";
import { isUserLoggedIn, getLoggedInUser } from "../../utils/helpers";
import HelpLauncher from "./HelpLauncher";
import HelpPanel from "./HelpPanel";
import { HELP_ASSISTANT_ENABLED, HELP_FAB_Z_INDEX } from "./constants";
import {
  toHistory,
  prefersReducedMotion,
  retryAfterSeconds,
  DAILY_LIMIT_MIN_WAIT_S,
} from "./helpers";
import {
  FAB_MARGIN_XS,
  PANEL_GAP,
  clampFabPosition,
  launcherBoxForWidth,
  panelPlacement,
  resolveFabPosition,
} from "./fabPosition";
import { getFabPosition, setFabPosition, setFabDismissed } from "./fabPersistence";
import useHelpFabDismissed from "./useHelpFabDismissed";
import useConsentObstruction, {
  CONSENT_BANNER_SELECTOR,
} from "./useConsentObstruction";
import useBlockingModalOpen from "./useBlockingModalOpen";
import useSegmentPanelObstruction from "./useSegmentPanelObstruction";
import useBuilderRailInset from "./useBuilderRailInset";
import useAppHeaderObstruction from "./useAppHeaderObstruction";
import useFabDrag from "./useFabDrag";
import useContentAvoidance from "./useContentAvoidance";

/**
 * In-app AI Help Assistant entry point (SPEC-v1 §7).
 *
 * Mounted once globally (App.js). Renders NOTHING unless BOTH:
 *   - the feature flag REACT_APP_AI_HELP_ASSISTANT === "true", and
 *   - a user is logged in (the endpoint is auth-required).
 * → zero behaviour change when the flag is OFF (default).
 *
 * Owns the client-side thread and the POST mutation; the panel owns the composer.
 *
 * It also owns the launcher's POSITION and DISMISSED state (Gordon's third-time
 * ask, 2026-07-30): the FAB is dragged anywhere on screen and stays there per
 * user, an X hides it, and the avatar/Profile menu's "Help" entry brings it
 * back. Geometry lives in fabPosition.js (pure), persistence in
 * fabPersistence.js, the pointer/keyboard drag in useFabDrag.js — this file
 * only wires them together.
 *
 * The launcher used to be `position: fixed; right; bottom`, which put it ON TOP
 * of the GDPR consent banner's Accept button at <=1024px (measured; see
 * fabPosition.js). It is now positioned by left/top and clamped above the
 * banner whenever the banner is mounted, so that overlap is impossible by
 * construction rather than tuned per breakpoint.
 */

/**
 * Current VISIBLE viewport, kept in sync with resize/orientation changes and
 * with the on-screen keyboard (T22.573). Chrome on Android resizes only the
 * visual viewport when the keyboard opens, so `height`/`offsetTop` come from
 * window.visualViewport when the page isn't pinch-zoomed.
 */
const useViewportSize = () => {
  const read = () => {
    if (typeof window === "undefined") return { width: 1440, height: 900, offsetTop: 0 };
    const vv = window.visualViewport;
    const unzoomed = vv && Math.abs((vv.scale || 1) - 1) < 0.01;
    return {
      width: window.innerWidth,
      height: unzoomed ? Math.min(window.innerHeight, vv.height) : window.innerHeight,
      offsetTop: unzoomed ? Math.max(0, vv.offsetTop || 0) : 0,
    };
  };
  const [size, setSize] = useState(read);
  useEffect(() => {
    const onResize = () =>
      setSize((prev) => {
        const next = read();
        return next.width === prev.width &&
          next.height === prev.height &&
          next.offsetTop === prev.offsetTop
          ? prev
          : next;
      });
    const vv = window.visualViewport;
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    vv?.addEventListener?.("resize", onResize);
    vv?.addEventListener?.("scroll", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      vv?.removeEventListener?.("resize", onResize);
      vv?.removeEventListener?.("scroll", onResize);
    };
  }, []);
  return size;
};

export default function HelpAssistant() {
  const location = useLocation(); // subscribe to navigation so the login gate re-checks on route change
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content, images?, sources?}
  const [error, setError] = useState(null); // {kind: 'generic'|'unavailable'|'busy'|'limit', retryAfter?} | null
  const [lastPayload, setLastPayload] = useState(null);

  const mutation = useHelpAssistant();

  // ---- launcher position / dismissal -------------------------------------
  const userId = getLoggedInUser()?.id ?? null;
  const dismissed = useHelpFabDismissed(userId);
  // The consent banner is bottom-docked, so it is cleared VERTICALLY.
  const obstructionHeight = useConsentObstruction();
  // gordon-glitch-help-button-covers-capture-rail-item-1024w: at tablet-
  // landscape builder heights (768-900px) the FAB's default corner lands on
  // top of the Segment Builder rail's item stack. The rail is a RIGHT-docked
  // full-height column, so it is cleared SIDEWAYS, not upwards —
  // fix-help-fab-covers-account-menu-1024w-short: clearing it upwards is what
  // ran the FAB into the app header and made the Account menu unclickable.
  // See fabPosition.js's railObstructionWidth for both measured viewports.
  const railInsetWidth = useBuilderRailInset();
  // The FAB may never be clamped onto the top app header, whatever pushes it
  // up. Closes the class, not just the rail instance (see
  // useAppHeaderObstruction.js).
  const topExclusionHeight = useAppHeaderObstruction();
  // fix-help-fab-still-covers-a-rail-item-after-5236: an obstruction can move
  // the FAB hundreds of px in one jump on resize. Animating that jump with the
  // normal 160ms easing sweeps the FAB across whatever it is clearing — i.e.
  // it "covers a rail item" for the transition's duration even though the
  // settled position is clear. MEASURED live (web-dev.iyuga.net/builder/5512,
  // resizing 1024x900 -> 1024x780): 16ms after resize the FAB was still
  // mid-sweep, overlapping the Assistant item at 367px^2. An obstruction-driven
  // move must snap instantly, the same way a drag does — only a FREE move (no
  // obstruction active) may ease.
  const obstructed = obstructionHeight > 0 || railInsetWidth > 0;
  // BUG-explore-398d6d: while a blocking modal dialog is open, the FAB (fixed,
  // z-index 13000, bottom-right corner) paints on top of the dialog's confirm
  // button and swallows its clicks. Hide the FAB entirely while any dialog is
  // open so it can never own a modal's pixels — the same react-to-the-
  // obstruction approach as the consent-banner fix above.
  //
  // stgfix-1005-P2 (MEASURED live web-dev.iyuga.net, fresh/not-yet-consented
  // session): the GDPR consent banner IS a genuine `role="dialog"
  // aria-modal="true"` surface, so without this ignoreSelector it ALSO
  // matched here and hid the FAB completely — not just repositioned it above
  // the banner as `useConsentObstruction` (above) already exists to do. Any
  // visitor who had not yet accepted/rejected cookies got zero Help FAB.
  const modalOpen = useBlockingModalOpen(CONSENT_BANNER_SELECTOR);
  // BUG-wheel-view-e4bc4c / BUG-wheel-view-6ae0ed: the mobile View-Wheel
  // open-segment panel is in-flow (not bottom-anchored), so it can't be
  // clamped above like the consent banner — the FAB hides instead. See
  // segmentPanelObstruction.js for the full writeup.
  const segmentPanelOpen = useSegmentPanelObstruction();
  const viewport = useViewportSize();
  // T22.227: phones get the narrow flush edge tab instead of the round FAB
  // (see launcherBoxForWidth in fabPosition.js for the measured overlap).
  const box = launcherBoxForWidth(viewport.width);
  const compact = box.edgeMargin === 0;
  // Re-read the persisted position whenever the user, the viewport or the
  // consent banner changes — a stale read is what would silently put the FAB
  // back over the banner after a resize.
  const [persistedTick, setPersistedTick] = useState(0);
  const resolved = useMemo(
    () =>
      resolveFabPosition({
        persisted: getFabPosition(userId),
        viewportWidth: viewport.width,
        viewportHeight: viewport.height,
        fabWidth: box.width,
        fabHeight: box.height,
        edgeMargin: box.edgeMargin,
        obstructionHeight,
        railInsetWidth,
        topExclusionHeight,
      }),
    // persistedTick is a deliberate dependency: it re-runs the localStorage
    // read after a drag commits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      userId,
      viewport.width,
      viewport.height,
      box.width,
      box.height,
      box.edgeMargin,
      obstructionHeight,
      railInsetWidth,
      topExclusionHeight,
      persistedTick,
    ]
  );

  const commitPosition = useCallback(
    (next) => {
      setFabPosition(userId, next);
      setPersistedTick((n) => n + 1);
    },
    [userId]
  );

  // T22.581: the phone edge tab slides along the edge, within the same bounds
  // the clamp allows, to the nearest spot with no control or text under it
  // (see contentAvoidance.js for the measured overlaps).
  const clampTop = (t) =>
    clampFabPosition({
      left: resolved.left,
      top: t,
      viewportWidth: viewport.width,
      viewportHeight: viewport.height,
      fabWidth: box.width,
      fabHeight: box.height,
      edgeMargin: box.edgeMargin,
      obstructionHeight,
      railInsetWidth,
      topExclusionHeight,
    }).top;
  const avoidable =
    HELP_ASSISTANT_ENABLED &&
    compact &&
    !dismissed &&
    !modalOpen &&
    !segmentPanelOpen &&
    isUserLoggedIn();
  const avoidTop = useContentAvoidance({
    enabled: avoidable && !open,
    // An open panel keeps the tab where the user tapped it.
    hold: avoidable && open,
    position: resolved,
    box,
    viewport,
    minTop: clampTop(-1e6),
    maxTop: clampTop(1e6),
  });
  // review-1008-Frontend-8051: the top held while the panel is open was
  // measured for the old viewport. MEASURED live web-stg main.cca54c68.js
  // 384x832: the tab held at 670, the keyboard left 420px visible, and the tab
  // (bottom 762) and the panel's text box (y 613) stayed under the keyboard.
  // Clamp it to the current viewport like the unavoided corner (T22.573).
  const placedTop = avoidTop == null ? null : clampTop(avoidTop);
  const placed = useMemo(
    () => (placedTop == null ? resolved : { left: resolved.left, top: placedTop }),
    [resolved, placedTop]
  );

  const drag = useFabDrag({
    position: placed,
    box,
    obstructionHeight,
    railInsetWidth,
    topExclusionHeight,
    onCommit: commitPosition,
  });

  const dismiss = useCallback(() => {
    setOpen(false);
    setFabDismissed(userId, true);
  }, [userId]);

  // Close on Escape while the panel is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const runMutation = useCallback(
    (payload) => {
      setError(null);
      mutation.mutate(payload, {
        onSuccess: (res) => {
          const data = res?.data || {};
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content:
                typeof data.answer === "string" && data.answer.trim()
                  ? data.answer
                  : "Sorry, I couldn't find an answer for that.",
              sources: Array.isArray(data.sources) ? data.sources : [],
            },
          ]);
        },
        onError: (err) => {
          const status = err?.response?.status;
          // stgqa-1005-kelly-list-sweep-b (Chat#3): MEASURED live on web-stg
          // (main.e27702ce.js, forced via page.route) that a 429 from
          // /api/v2/help-assistant/ fell into the generic "Something went
          // wrong" bucket below, identical to any other failure -- nothing
          // told the user they'd been rate-limited specifically.
          // T22.569: a 429 whose retry_after is hours is the daily cap, not
          // the per-minute burst -- "try again in a moment" misled users.
          const wait = status === 429 ? retryAfterSeconds(err) : null;
          if (wait !== null && wait > DAILY_LIMIT_MIN_WAIT_S) {
            setError({ kind: "limit", retryAfter: wait });
            return;
          }
          setError({
            kind:
              status === 404 ? "unavailable" : status === 429 ? "busy" : "generic",
          });
        },
      });
    },
    [mutation]
  );

  const sendMessage = useCallback(
    (question, images = []) => {
      const trimmed = (question || "").trim();
      if (!trimmed || mutation.isLoading) return;

      const history = toHistory(messages);
      // optimistic: show the user's turn immediately
      setMessages((prev) => [
        ...prev,
        { role: "user", content: trimmed, images },
      ]);

      const payload = { question: trimmed };
      if (images && images.length) payload.images = images.slice(0, 4);
      if (history.length) payload.history = history;

      setLastPayload(payload);
      runMutation(payload);
    },
    [messages, mutation.isLoading, runMutation]
  );

  const retry = useCallback(() => {
    if (lastPayload && !mutation.isLoading) runMutation(lastPayload);
  }, [lastPayload, mutation.isLoading, runMutation]);

  const toggle = useCallback(() => {
    // A drag ends with a synthetic click on the FAB; it must not also open the
    // panel the user was only trying to move.
    if (drag.suppressClick()) return;
    setOpen((v) => !v);
  }, [drag]);

  // Gates. Keep hooks above this line so their order never changes.
  if (!HELP_ASSISTANT_ENABLED) return null;
  if (!isUserLoggedIn()) return null;
  // Dismissed by this user — the ONLY way back is the Profile menu's "Help".
  if (dismissed) return null;
  // A blocking modal dialog is open — hide the FAB so it cannot cover the
  // dialog's confirm button (BUG-explore-398d6d). It returns as soon as the
  // dialog closes.
  if (modalOpen) return null;
  // A View-Wheel mobile segment panel is open — never sit on top of its
  // content (BUG-wheel-view-e4bc4c / BUG-wheel-view-6ae0ed).
  if (segmentPanelOpen) return null;
  // On the full-screen Messages inbox/thread, the DM composer's Send button
  // lives in the bottom-right corner — exactly where this FAB rests by default.
  // The FAB (fixed, z-index 13000) painted ON TOP of Send and swallowed its
  // clicks, opening Help instead of sending the message (ISP-9999, measured
  // live web-stg main.3c024cd6.js). Suppress the launcher on /messages* so the
  // composer's primary action is always reachable — the SAME route-suppression
  // the corner MiniDmDock already applies to itself (ChatDock/MiniDmDock.jsx,
  // comment "O7"). The FAB is unaffected on every other route, and the Profile
  // menu's "Help" restore entry still works.
  if (/^\/messages(\/|$)/.test(location.pathname || "")) return null;

  const reduceMotion = prefersReducedMotion();
  const { left, top } = drag.position;
  // Anchor the panel so it stays on screen wherever the FAB was dropped, and
  // cap its height to the visible room (T22.573: the phone keyboard).
  const { anchorBelow, maxHeight: panelMaxHeight } = panelPlacement({
    top,
    fabHeight: box.height,
    viewportHeight: viewport.height,
  });
  const anchorLeftEdge = left < viewport.width / 2;

  return (
    <Box
      data-testid="help-fab-root"
      sx={{
        position: "fixed",
        left: `${left}px`,
        // Fixed boxes sit in the layout viewport; follow the visual one when
        // the keyboard has panned it.
        top: `${top + viewport.offsetTop}px`,
        width: `${box.width}px`,
        height: `${box.height}px`,
        zIndex: HELP_FAB_Z_INDEX,
        pointerEvents: "none", // children re-enable; lets clicks pass through the gap
        // Moving to a clamped position because the user dragged/dropped it,
        // or because it returned to its free corner, animates. A drag must
        // track the pointer with zero lag, and an obstruction-driven clamp
        // (consent banner, builder rail) must snap instantly rather than
        // ease across whatever it's clearing — see the `obstructed` comment
        // above for the measured sweep this avoids.
        transition:
          reduceMotion || drag.dragging || obstructed
            ? "none"
            : "left 160ms ease-out, top 160ms ease-out",
      }}
    >
      <Grow
        in={open}
        timeout={reduceMotion ? 0 : 180}
        style={{
          transformOrigin: `${anchorBelow ? "top" : "bottom"} ${
            anchorLeftEdge ? "left" : "right"
          }`,
        }}
      >
        <Box
          data-testid="help-fab-panel-anchor"
          sx={{
            position: "absolute",
            pointerEvents: "auto",
            ...(anchorBelow
              ? { top: `${box.height + PANEL_GAP}px` }
              : { bottom: `${box.height + PANEL_GAP}px` }),
            // The phone tab is flush to the edge, so right:0 would put the
            // calc(100vw - 32px) panel against the screen edge (32px gap on
            // the left, none on the right). Inset it to keep 16px each side.
            ...(anchorLeftEdge
              ? { left: 0 }
              : { right: compact ? `${FAB_MARGIN_XS}px` : 0 }),
          }}
        >
          {open && (
            <HelpPanel
              messages={messages}
              pending={mutation.isLoading}
              error={error}
              onSend={sendMessage}
              onRetry={retry}
              maxHeight={panelMaxHeight}
              onClose={() => setOpen(false)}
              // T22.440: on a phone the panel covers the Help page the source
              // chip just opened, so close it there; desktop keeps the chat.
              onSourceClick={() => {
                if (compact) setOpen(false);
              }}
            />
          )}
        </Box>
      </Grow>
      <Box sx={{ pointerEvents: "auto" }}>
        <HelpLauncher
          open={open}
          onToggle={toggle}
          onDismiss={dismiss}
          dragHandlers={drag.handlers}
          dragging={drag.dragging}
          reduceMotion={reduceMotion}
          compact={compact}
        />
      </Box>
    </Box>
  );
}
